import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  clearAllowance,
  credits,
  readAllowances,
  resolveRequest,
  setAllowance,
  type AllowancePerson,
  type AllowanceRequest,
  type Allowances,
} from '@shared/services/allowanceService'
import './adminPage.css'
import './allowances.css'

/**
 * Allowances (Huanrui, 2026-10-19), an administrator's card
 * -------------------------------------------------------------
 * How much each person may spend a month, in credits (1,000 = $1): one
 * allowance for everybody, and any person's own in its place (an empty box
 * for a person means "no limit for them"). What each has spent this month and
 * has left. And the requests for more, at the top while any wait: raise to a
 * new allowance, or decline.
 *
 * A run that goes over is not stopped; the next one is (allowance.ts).
 */

export const ALLOWANCES_ROUTE = '/allowances'

/** '' for none; otherwise a whole number of credits. Undefined: not a number. */
function parseCredits(text: string): number | null | undefined {
  const clean = text.replace(/[,\s]/g, '')
  if (!clean) return null
  const n = Number(clean)
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : undefined
}

const usd = (n: number) => `$${(n / 1000).toFixed(2)}`

function when(iso: string): string {
  const t = new Date(iso)
  return Number.isNaN(t.getTime()) ? '' : t.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function AllowancesPage() {
  const [data, setData] = useState<Allowances | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [search, setSearch] = useState('')
  const [everyone, setEveryone] = useState('')

  const read = useCallback(() => {
    readAllowances()
      .then((found) => {
        setData(found)
        setEveryone(found.everyone === null ? '' : String(found.everyone))
      })
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : 'That could not be read'))
  }, [])
  useEffect(read, [read])

  const act = async (key: string, work: () => Promise<unknown>) => {
    setBusy(key)
    setError('')
    try {
      await work()
      read()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be done')
    } finally {
      setBusy('')
    }
  }

  const waiting = useMemo(() => (data?.requests ?? []).filter((one) => !one.resolvedAt), [data])
  const answered = useMemo(() => (data?.requests ?? []).filter((one) => one.resolvedAt).slice(0, 20), [data])
  const people = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (data?.people ?? [])
      .filter((one) => !q || one.email.includes(q) || one.name.toLowerCase().includes(q))
      .sort((a, b) => b.used - a.used || a.email.localeCompare(b.email))
  }, [data, search])

  const everyoneValue = parseCredits(everyone)

  return (
    <section className="adm alw">
      <div className="adm__head">
        <h1>Allowances</h1>
        <p className="adm__small">Monthly, in credits: 1,000 = $1. A new month starts everyone afresh.</p>
      </div>
      {error ? <p className="adm__error">{error}</p> : null}

      {waiting.length > 0 ? (
        <div className="adm__card alw__requests">
          <p className="adm__label">Asking for more · {waiting.length}</p>
          {waiting.map((one) => (
            <RequestRow key={one.id} request={one} busy={busy === one.id} onAct={(work) => void act(one.id, work)} />
          ))}
        </div>
      ) : null}

      <div className="adm__card">
        <p className="adm__label">Everybody</p>
        <div className="alw__line">
          <input
            className="adm__input alw__num"
            inputMode="numeric"
            placeholder="No limit"
            value={everyone}
            onChange={(event) => setEveryone(event.target.value)}
            aria-label="Everybody's monthly allowance in credits"
          />
          <span className="adm__small">
            credits a month{everyoneValue ? ` · ${usd(everyoneValue)}` : ''} · for everyone without their own
          </span>
          <span className="alw__gap" />
          <button
            type="button"
            className="adm__go"
            disabled={busy === '*' || everyoneValue === undefined || everyoneValue === (data?.everyone ?? null)}
            onClick={() => void act('*', () => (everyoneValue === null ? clearAllowance('*') : setAllowance('*', everyoneValue!)))}
          >
            Save
          </button>
        </div>
      </div>

      <div className="adm__card">
        <div className="alw__line">
          <p className="adm__label">People · this month</p>
          <span className="alw__gap" />
          <input
            className="adm__input alw__search"
            type="search"
            placeholder="Search people"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="alw__table" role="table">
          <div className="alw__tr alw__tr--head" role="row">
            <span>Person</span>
            <span>Spent</span>
            <span>Allowance</span>
            <span>Left</span>
            <span />
          </div>
          {people.map((one) => (
            <PersonRow
              key={one.email}
              person={one}
              everyone={data?.everyone ?? null}
              busy={busy === one.email}
              onAct={(work) => void act(one.email, work)}
            />
          ))}
          {data && people.length === 0 ? <p className="adm__small">Nobody matches.</p> : null}
          {!data && !error ? <p className="adm__small">Reading…</p> : null}
        </div>
      </div>

      {answered.length > 0 ? (
        <div className="adm__card">
          <p className="adm__label">Answered</p>
          {answered.map((one) => (
            <p key={one.id} className="adm__small">
              {one.name || one.email} · asked {when(one.askedAt)} at {credits(one.used)}
              {one.allowance !== null ? ` of ${credits(one.allowance)}` : ''} · {one.outcome === 'raised' ? 'raised' : 'declined'}{' '}
              {one.resolvedAt ? when(one.resolvedAt) : ''}
            </p>
          ))}
        </div>
      ) : null}
    </section>
  )
}

function RequestRow({ request, busy, onAct }: { request: AllowanceRequest; busy: boolean; onAct: (work: () => Promise<unknown>) => void }) {
  const suggested = request.allowance !== null ? Math.max(request.allowance * 2, request.usedNow + 1000) : request.usedNow + 5000
  const [to, setTo] = useState(String(suggested))
  const value = parseCredits(to)
  return (
    <div className="alw__req">
      <div className="alw__req-who">
        <strong>{request.name || request.email}</strong>
        <span className="adm__small">
          {request.email} · asked {when(request.askedAt)} · spent {credits(request.usedNow)}
          {request.allowance !== null ? ` of ${credits(request.allowance)}` : ''}
        </span>
        {request.note ? <span className="alw__note">“{request.note}”</span> : null}
      </div>
      <div className="alw__line">
        <input
          className="adm__input alw__num"
          inputMode="numeric"
          placeholder="No limit"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          aria-label="New monthly allowance"
        />
        <button
          type="button"
          className="adm__go"
          disabled={busy || value === undefined}
          onClick={() => onAct(() => resolveRequest(request.id, 'raised', value ?? null))}
        >
          {value === null ? 'No limit' : 'Raise'}
        </button>
        <button type="button" className="alw__quiet-btn" disabled={busy} onClick={() => onAct(() => resolveRequest(request.id, 'declined'))}>
          Decline
        </button>
      </div>
    </div>
  )
}

function PersonRow({
  person,
  everyone,
  busy,
  onAct,
}: {
  person: AllowancePerson
  everyone: number | null
  busy: boolean
  onAct: (work: () => Promise<unknown>) => void
}) {
  const [text, setText] = useState(person.hasOwn ? (person.own === null || person.own === undefined ? '' : String(person.own)) : '')
  useEffect(() => {
    setText(person.hasOwn ? (person.own === null || person.own === undefined ? '' : String(person.own)) : '')
  }, [person.hasOwn, person.own])
  const value = parseCredits(text)
  const changed = person.hasOwn ? value !== (person.own ?? null) : text.trim() !== ''
  const out = person.left !== null && person.left <= 0
  return (
    <div className={`alw__tr${out ? ' alw__tr--out' : ''}`} role="row">
      <span className="alw__who">
        <strong>{person.name || person.email}</strong>
        {person.name ? <em>{person.email}</em> : null}
      </span>
      <span>{credits(person.used)}</span>
      <span>
        <input
          className="adm__input alw__num"
          inputMode="numeric"
          placeholder={person.hasOwn ? 'No limit' : everyone === null ? 'Everybody’s: none' : `Everybody’s: ${credits(everyone)}`}
          value={text}
          onChange={(event) => setText(event.target.value)}
          aria-label={`${person.name || person.email}'s own monthly allowance`}
        />
      </span>
      <span>{person.left === null ? '∞' : credits(person.left)}</span>
      <span className="alw__acts">
        <button
          type="button"
          className="adm__go"
          disabled={busy || !changed || value === undefined}
          onClick={() => onAct(() => setAllowance(person.email, value ?? null))}
          title={value === null ? 'Save as no limit for this person' : 'Save this person’s own allowance'}
        >
          Save
        </button>
        {person.hasOwn ? (
          <button type="button" className="alw__quiet-btn" disabled={busy} onClick={() => onAct(() => clearAllowance(person.email))} title="Back to everybody's">
            Use everybody’s
          </button>
        ) : null}
      </span>
    </div>
  )
}

export default AllowancesPage
